import { LazyQueryHookOptions } from "@apollo/client"
import { IconName } from "@blueprintjs/core"
import { useLocalStorage } from "@mantine/hooks"

import { MeQuery, MeQueryVariables, useMeLazyQuery } from "generated/graphql"
import React, { createContext, useEffect } from "react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import appRoutes from "routes"
import AppToaster from "utils/AppToaster"
import { tracker } from "../../App"
import CustomSpinner from "../Atoms/CustomSpinner"
export interface AuthenticationProps {
  /*
    The information about the current authenticated user
    firstName,lastName etc.
  */
  user?: MeQuery["me"]["user"] | null
  /*
    The information about the permissions given to the authenticated user
    array of strings
  */
  permissions?: (string | null)[] | null
  /**
   * The distribution centeres the current user is a part of
   */
  distributionCentres?: MeQuery["me"]["distributionCentres"]
  /*
    the Bearer token of the current session
  */
  token?: string | null
  /*
    a callback given to logout the user as a form of convinience
  */
  logout?: () => void
}
// List of authentication checks (the user is logged out whenever one of these messages)
// are received as the response
interface AuthCode {
  /**
   * The main code (error message) that will be compared to
   */
  code: string
  /**
   * The custom human readable message that will be shown to the user
   */
  message: string
  /**
   * The descriptive icon which will help clarify things
   */
  icon?: IconName
}

export const AUTHENTICATION_CHECKS: AuthCode[] = [
  {
    code: "Signature has expired",
    message: "The token has expired, please log-in to continue",
    icon: "stopwatch",
  },
  {
    code: "You do not have permission to perform this action",
    message:
      "You do not have permission to perform this action, please log-in with an appropriate account",
    icon: "ban-circle",
  },
  {
    code: "Error decoding signature",
    message: "Invalid token, Please login again",
    icon: "error",
  },
]

const AuthenticationContext = createContext<AuthenticationProps>({})

interface AuthProps {
  options?: LazyQueryHookOptions<MeQuery, MeQueryVariables>
  children?: React.ReactNode
}

const unprotectedRoutes = appRoutes.filter(mr => !mr.protected).map(r => r.path)
const Authentication = ({ children, options }: AuthProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [token] = useLocalStorage({ key: "token" })

  const [fetchUserData, { data, loading, error }] = useMeLazyQuery(options)

  useEffect(() => {
    // Only fetch user details if a token is available in local storage
    if (token) {
      fetchUserData()
    }
  }, [token, fetchUserData])

  const logout = () => {
    localStorage.clear()
    navigate("/")
  }

  useEffect(() => {
    if (
      data &&
      data?.me &&
      parseInt(process.env.REACT_APP_USE_OPEN_RELAY || "")
    ) {
      const user = data?.me?.user
      tracker.setUserID(user?.username || "")
      tracker.setMetadata("name", user?.fullName || "")
      tracker.setMetadata("email", user?.email || "")
    }

    if (data?.["organisationDetails"] === null) {
      localStorage.clear()
      AppToaster.show({
        message: "You are not authorized, please login again",
        intent: "danger",
      })
      window.location.pathname = "/"
    }
  }, [data])

  // Remove the authentication check from all the unprotected routes
  if (
    unprotectedRoutes.includes(location.pathname) ||
    location.pathname.includes("/track")
  ) {
    return <>{children}</>
  }

  if (!localStorage.getItem("token")) {
    return <Navigate to={"/"} />
  }
  if (loading) return <CustomSpinner />

  if (data?.me)
    return (
      <AuthenticationContext.Provider
        value={{
          distributionCentres: data?.me?.distributionCentres,
          user: data?.me?.user,
          permissions: data?.me?.permissions ?? [],
          token,
          logout,
        }}
      >
        {children}
      </AuthenticationContext.Provider>
    )
  if (error) {
    error.graphQLErrors.forEach(({ message }) => {
      if (AUTHENTICATION_CHECKS.find(ac => ac.code.includes(message))) {
        AppToaster.show({
          message,
          intent: "warning",
          icon: "warning-sign",
        })
        return <Navigate to={"/"} />
      }
    })
  }
  return (
    <div className="bg-gray-100 h-screen w-full flex justify-center items-center">
      <h6 className="text-gray-100">
        Unable to fetch user details, please make sure you are connected to the
        internet
      </h6>
    </div>
  )
}

export default Authentication
export { AuthenticationContext }
